if (!customElements.get('atelier-set-builder')) {
  customElements.define('atelier-set-builder', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.busy = false;
      this.button = this.querySelector('[data-add-set]');
      this.total = this.querySelector('[data-set-total]');
      this.summary = this.querySelector('[data-set-summary]');
      this.error = this.querySelector('[data-set-error]');
      this.status = this.querySelector('[data-set-status]');
      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
          style: 'currency', currency: this.dataset.currency,
        });
        this.rows = [...this.querySelectorAll('[data-set-item]')].map((element) => {
          const row = {
            element, toggle: element.querySelector('[data-piece-toggle]'),
            select: element.querySelector('[data-piece-variant]'),
            price: element.querySelector('[data-piece-price]'),
            availability: element.querySelector('[data-piece-availability]'),
            variants: JSON.parse(element.querySelector('[data-piece-variants]').textContent),
          };
          row.variant = row.variants.find((variant) => String(variant.id) === row.toggle.value) || null;
          return row;
        });
      } catch {
        this.hidden = false;
        this.showError('Set pricing is temporarily unavailable. Please choose a necklace from its product page.');
        return;
      }
      this.current = this.rows.find((row) => row.element.hasAttribute('data-current-piece'));
      this.primary = document.querySelector('product-info[id^="MainProduct-"]');
      this.onChange = (event) => {
        const row = this.rows.find((item) => item.element.contains(event.target));
        if (row?.select === event.target) {
          row.variant = row.variants.find((variant) => String(variant.id) === row.select.value) || null;
          if (!row.variant?.available) row.toggle.checked = false;
        }
        this.clearFeedback();
        this.render();
      };
      this.onClick = (event) => {
        const preset = event.target.closest('[data-set-preset]');
        if (preset && this.contains(preset) && !preset.disabled) this.choosePreset(preset.dataset.setPreset);
        if (event.target.closest('[data-add-set]') === this.button) this.addSelection();
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      if (this.primary) {
        this.onPrimaryChange = () => this.syncCurrent();
        this.primary.addEventListener('change', this.onPrimaryChange);
        this.observer = new MutationObserver(() => this.syncCurrent());
        this.observer.observe(this.primary, {
          subtree: true, childList: true, attributes: true,
          attributeFilter: ['value', 'disabled', 'aria-disabled'],
        });
      }
      this.syncCurrent();
      this.hidden = false;
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.primary?.removeEventListener('change', this.onPrimaryChange);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.initialized = false;
    }
    get selected() {
      return this.rows.filter((row) => row.toggle.checked && row.variant?.available && !row.pending);
    }
    syncCurrent() {
      if (this.current && this.primary) {
        const input = this.primary.querySelector('product-form [name="id"]');
        const primaryButton = this.primary.querySelector('product-form button[type="submit"]');
        this.current.variant = this.current.variants.find((variant) => String(variant.id) === input?.value) || null;
        this.current.pending = primaryButton?.disabled || primaryButton?.getAttribute('aria-disabled') === 'true';
        if (!this.current.variant?.available) this.current.toggle.checked = false;
        const option = this.current.element.querySelector('[data-current-option]');
        if (option) option.textContent = this.current.variants.length > 1 ? this.current.variant?.title || '' : '';
      }
      this.render();
    }
    offerPricing() {
      const rows = this.selected;
      const original = rows.reduce((sum, row) => sum + row.variant.price, 0);
      const unit = Number(this.dataset.offerUnit);
      const target = rows.length === 2 ? Number(this.dataset.offerPair) : rows.length === 3 ? Number(this.dataset.offerTrio) : NaN;
      const applies = this.dataset.currency === 'INR' && rows.every(row => row.variant.price === unit)
        && Number.isSafeInteger(unit) && unit > 0 && Number.isSafeInteger(target) && target > 0 && target < original;
      return {original, total: applies ? target : original, savings: applies ? original - target : 0};
    }
    render() {
      if (!this.rows) return;
      for (const row of this.rows) {
        const available = Boolean(row.variant?.available);
        row.toggle.disabled = this.busy || !available || Boolean(row.pending);
        if (row.select) row.select.disabled = this.busy;
        row.toggle.value = row.variant ? String(row.variant.id) : '';
        row.price.textContent = row.variant ? this.money.format(row.variant.price / 100) : 'Unavailable';
        row.availability.textContent = !available ? this.dataset.soldOut || 'Sold out' : row.pending ? 'Updating…' : '';
        row.element.classList.toggle('is-selected', row.toggle.checked && available);
        row.element.classList.toggle('is-unavailable', !available);
      }
      const selected = this.selected;
      const count = selected.length;
      const pricing = this.offerPricing();
      this.total.textContent = this.money.format(pricing.total / 100);
      const original = this.querySelector('[data-set-original]');
      const savings = this.querySelector('[data-set-savings]');
      if (original) { original.hidden = !pricing.savings; original.textContent = this.money.format(pricing.original / 100); }
      if (savings) { savings.hidden = !pricing.savings; savings.textContent = `Save ${this.money.format(pricing.savings / 100)} · Offer applied in your bag`; }
      this.summary.textContent = count === 0 ? 'Choose two necklaces to make a pair.' : count === 1 ? 'Choose one more necklace to make a pair.' : count === 2 ? 'Your pair · one of each selected necklace.' : 'The complete trilogy · one of each necklace.';
      this.button.disabled = this.busy || count < 2;
      const label = this.busy ? 'Adding your selection…' : count === 3 ? 'Add trilogy to bag' : count === 2 ? 'Add pair to bag' : count === 0 ? 'Choose your necklaces' : 'Choose another necklace';
      const labelElement = this.button.querySelector('[data-set-button-label]');
      if (labelElement) labelElement.textContent = label;
      else this.button.textContent = label;
      const available = this.rows.filter((row) => row.variant?.available && !row.pending);
      for (const preset of this.querySelectorAll('[data-set-preset]')) {
        const kind = preset.dataset.setPreset;
        preset.disabled = this.busy || (kind === 'single' ? !this.current?.variant?.available || Boolean(this.current?.pending) : kind === 'pair' ? available.length < 2 : available.length < 3);
        const active = kind === 'single' ? count === 1 && selected[0] === this.current : kind === 'pair' ? count === 2 : count === 3;
        preset.setAttribute('aria-pressed', String(active));
      }
    }
    choosePreset(kind) {
      if (this.busy) return;
      const available = this.rows.filter((row) => row.variant?.available && !row.pending);
      const chosen = kind === 'single' ? available.filter((row) => row === this.current) : kind === 'pair' ? available.slice(0, 2) : available;
      for (const row of this.rows) row.toggle.checked = chosen.includes(row);
      this.clearFeedback();
      this.render();
    }
    clearFeedback() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.status.textContent = '';
    }
    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
    navigateToCart() {
      window.location.assign(this.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`);
    }
    async addSelection() {
      if (this.busy || this.selected.length < 2 || this.button.disabled) return;
      const items = this.selected.map((row) => ({id: String(row.variant.id), quantity: 1}));
      if (this.hasAttribute('data-preview')) {
        this.dispatchEvent(new CustomEvent('atelier:preview-set', {bubbles: true, detail: {items}}));
        return;
      }
      this.busy = true;
      this.clearFeedback();
      this.setAttribute('aria-busy', 'true');
      this.render();
      // Dawn's single-product notification requires one line-item key. A set
      // opens the full drawer, or goes to the cart when the drawer is disabled.
      let cart = document.querySelector('cart-drawer');
      let sections = [];
      try {
        sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
        cart?.setActiveElement?.(this.button);
      } catch { cart = null; }
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items, sections, sections_url: window.location.pathname}),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Your selection could not be added. Please try again.');
        if (!cart || !sections.length || typeof cart.renderContents !== 'function' || sections.some((section) => !data.sections?.[section])) {
          this.navigateToCart();
          return;
        }
        try {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        } catch {
          this.navigateToCart();
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {source: 'atelier-set-builder', cartData: data})).catch(() => {}); } catch { /* The cart was added; analytics subscribers must not turn success into a retry. */ }
        }
        this.status.textContent = `${items.length} necklaces added to your bag.`;
        this.dispatchEvent(new CustomEvent('atelier:set-added', {bubbles: true, detail: {count: items.length}}));
      } catch (error) {
        this.showError(error.message || 'Your selection could not be added. Please try again.');
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        this.syncCurrent();
      }
    }
  });
}
